import React, { useState } from "react";
import type { Restaurant } from "../restaurant.types";
import { FaPhotoFilm, FaPlus, FaUpload, FaX } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

const ImageCard = ({
  imageUrl,
  title = "",
  subtitle = "Home in Nairobi, Nairobi County",
  showBadge,
  isArchived,
  onClick,
}) => {
  return (
    <div
      className="w-full overflow-hidden bg-white pb-2 border-b border-b-gray-300 cursor-pointer transition-all duration-300"
      aria-label={title}
      onClick={onClick}
    >
      <div className="relative w-full h-52 bg-white md:h-84 bg-[#FFF8E7] rounded-xl border border-[var(--border)] overflow-hidden hover:cursor-pointer hover:opacity-85 ">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          // Fallback background color when no image
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: "#FFF8E7" }}
          >
            <div className="text-4xl text-gray-400">
              <FaPhotoFilm />
            </div>
          </div>
        )}

        {(showBadge || isArchived) && (
          <span className="absolute top-3 left-3 inline-flex items-center px-3 py-2 rounded-full bg-white/90 text-sm font-semibold text-gray-800 shadow">
            <span className="w-2 h-2 bg-red-600 rounded-full mr-2" />
            {isArchived ? "Archived" : "Action required"}
          </span>
        )}

        {/* Edit overlay on hover */}
        <div className="absolute transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 hover:opacity-100 transition-opacity duration-300 text-white text-lg font-semibold">
            Click to Edit
          </div>
        </div>
      </div>

      <div className="pt-2">
        <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
          {title}
        </h3>
        <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
      </div>
    </div>
  );
};

interface RestaurantListProps {
  restaurants: Restaurant[];
  onCreateRestaurant: () => void;
  loading: boolean
}

const RestaurantList: React.FC<RestaurantListProps> = ({
  restaurants,
  onCreateRestaurant,
  loading,
}) => {
  const navigate = useNavigate();
  const handleRestaurantClick = (restaurant: Restaurant) => {
    navigate(`/dashboard/edit/${restaurant._id}`)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white md:px-10">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-2 lg:px-8 md:py-8">
          <div className="flex justify-between items-center mb-4 md:mb-8">
            <div>
              <h2 className="text-3xl font-bold text-[#111827]">
                Your Restaurants
              </h2>
              <p className="text-[#5E503F] mt-2">
                Manage and view your restaurant listings
              </p>
            </div>
            <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="w-full h-52 md:h-84 bg-gray-200 rounded-xl"></div>
                <div className="pt-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white md:px-10">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-2 lg:px-8 md:py-8">
        {/* Your Restaurants Section */}
        <section className="mb-12">
          <div className="flex justify-between items-center mb-4 md:mb-8">
            <div>
              <h2 className="text-3xl font-bold text-[#111827]">
                Your Restaurants
              </h2>
              <p className="text-[#5E503F] mt-2">
                Manage and view your restaurant listings
              </p>
            </div>

            <button
              onClick={onCreateRestaurant}
              className="p-3 bg-[#FFD166] text-[#111827] font-semibold rounded-lg hover:bg-[#E1AD01] transition-colors flex items-center gap-2"
            >
              <FaPlus />
            </button>
          </div>

          {restaurants.length === 0 ? (
            <div className="bg-[#FFF8E7] rounded-2xl p-12 text-center border-2 border-dashed border-[#FFDDA6]">
              <div className="text-6xl mb-4">🏪</div>
              <h3 className="text-2xl font-bold text-[#111827] mb-4">
                Reveal your hidden gem
              </h3>
              <p className="text-[#5E503F] mb-6 max-w-md mx-auto">
                Share your story, your ambiance, and let the world discover your
                hidden gem.
              </p>
              <button
                onClick={onCreateRestaurant}
                className="px-8 py-4 bg-[#FFD166] text-[#111827] font-bold text-lg rounded-xl hover:bg-[#E1AD01] transition-all duration-300 transform hover:scale-105"
              >
                Add Your Gem
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {restaurants.map((restaurant) => (
                <ImageCard
                  key={restaurant._id}
                  imageUrl={restaurant?.gallery?.cover?.url || ""}
                  title={restaurant?.name}
                  subtitle={[
                    restaurant?.location?.address, restaurant?.location?.area, restaurant?.location?.city
                  ].filter(Boolean).join(", ")  || "No address provided"}
                  showBadge={!restaurant?.isApproved}
                  isArchived={restaurant.isArchived}
                  onClick={() => handleRestaurantClick(restaurant)}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default RestaurantList;
